export function selectHomeTwinBuilding(buildings, storedId) {
  const options = (buildings || []).filter(item => item?.buildingId && item?.modelId)
    .slice().sort((a, b) => String(a.buildingName || '').localeCompare(String(b.buildingName || ''), 'de') || String(a.buildingId).localeCompare(String(b.buildingId)));
  return options.find(item => String(item.buildingId) === String(storedId || '')) || options[0] || null;
}

export function homeTwinHeatAreas(document, snapshot, floorRef = null) {
  const model = document?.model || {}, rooms = (model.objects || []).filter(object => object.kind === 'room');
  const floors = (model.floors || []).map((floor, index) => ({ ...floor, order: index }))
    .sort((a, b) => Number.isFinite(a.z) && Number.isFinite(b.z) ? a.z - b.z : a.order - b.order);
  const floorOrder = new Map(floors.map((floor, index) => [floor.ref, index]));
  const areas = new Map();
  for (const room of rooms) {
    if (!room.heatAreaRef) continue;
    if (!areas.has(room.heatAreaRef)) areas.set(room.heatAreaRef, { heatAreaRef: room.heatAreaRef, rooms: [] });
    areas.get(room.heatAreaRef).rooms.push(room);
  }
  for (const state of snapshot?.heatAreas || []) {
    const current = areas.get(state.heatAreaRef) || { heatAreaRef: state.heatAreaRef, rooms: rooms.filter(room => (state.roomRefs || []).includes(room.ref)) };
    areas.set(state.heatAreaRef, { ...current, ...state, rooms: current.rooms });
  }
  return [...areas.values()].filter(area => !floorRef || area.rooms.some(room => room.floorRef === floorRef))
    .map(area => {
      const first = area.rooms[0];
      const floorNames = [...new Set(area.rooms.map(room => model.floors?.find(floor => floor.ref === room.floorRef)?.name).filter(Boolean))];
      return { ...area, name: area.name || (first?.heatAreaLocalId != null ? `HeatArea ${first.heatAreaLocalId}` : 'Heizbereich'),
        roomNames: [...new Set(area.rooms.map(room => room.name).filter(name => name && name !== first?.apartmentName))].join(', '),
        floorOrder: Math.min(...area.rooms.map(room => floorOrder.has(room.floorRef) ? floorOrder.get(room.floorRef) : Infinity)),
        apartmentName: first?.apartmentName || '', floorNames: floorNames.join(', ') };
    }).sort((a, b) => a.floorOrder - b.floorOrder || `${a.apartmentName} ${a.name}`.localeCompare(`${b.apartmentName} ${b.name}`, 'de', { numeric: true }));
}

export function validHomeTwinFloor(document, floorRef) {
  return (document?.model?.floors || []).some(floor => floor.ref === floorRef) ? floorRef : null;
}

export function homeTwinApartmentSelection(document, selection) {
  const apartmentRef = typeof selection === 'string' ? selection : selection?.apartmentRef;
  if (!apartmentRef) return null;
  const floors = document?.model?.floors || [];
  const rooms = (document?.model?.objects || []).filter(room => room.kind === 'room' && room.apartmentRef === apartmentRef && floors.some(floor => floor.ref === room.floorRef));
  if (selection?.floorRef && !rooms.some(room => room.floorRef === selection.floorRef)) return null;
  const selected = rooms.find(room => room.floorRef === selection?.floorRef) || rooms.slice().sort((a, b) => {
    const first = floors.find(floor => floor.ref === a.floorRef), second = floors.find(floor => floor.ref === b.floorRef);
    return Number.isFinite(first.z) && Number.isFinite(second.z) ? first.z - second.z : floors.indexOf(first) - floors.indexOf(second);
  })[0];
  return selected ? { apartmentRef, apartmentName: selected.apartmentName || 'Appartement', floorRef: selected.floorRef } : null;
}

const heatingNames = { 0: 'Bodenheizung', 1: 'Wandheizung außen', 2: 'Heizkörper', 3: 'Wandheizung innen' };
const knownHeatingType = value => Number.isInteger(value) && Object.prototype.hasOwnProperty.call(heatingNames, value);
export function homeTwinHeatingTypeLabel(value) { return knownHeatingType(value) ? heatingNames[value] : 'Heizungsart unbekannt'; }
export function homeTwinHeatingType(objects, heatAreaRef, fallback) {
  const supplied = objects.filter(object => object.kind === 'heatarea' && object.heatAreaRef === heatAreaRef)
    .map(object => object.heating_type).filter(value => value != null);
  if (supplied.some(value => !knownHeatingType(value))) return null;
  const types = new Set(supplied);
  if (types.size > 1) return null;
  return types.size === 1 ? [...types][0] : knownHeatingType(fallback) ? fallback : null;
}
// Room readings and surface estimates stay separate. Geometry supplies names
// and associations only, never a replacement temperature or power value.
export function homeTwinRoomGroups(document, snapshot, floorRef) {
  if (!validHomeTwinFloor(document, floorRef)) return [];
  const objects = document?.model?.objects || [], modelRooms = objects.filter(room => room.kind === 'room' && room.floorRef === floorRef);
  const roomStates = new Map((snapshot?.rooms || []).map(room => [room.ref, room]));
  const areaStates = new Map((snapshot?.heatAreas || []).map(area => [area.heatAreaRef, area]));
  const ownerLabel = ref => {
    if (!ref) return null;
    const roomRefs = new Set(areaStates.get(ref)?.roomRefs || []);
    objects.filter(object => object.kind === 'heatarea' && object.heatAreaRef === ref).forEach(object => roomRefs.add(object.roomRef));
    return objects.find(room => room.kind === 'room' && (room.heatAreaRef === ref || roomRefs.has(room.ref) || (roomStates.get(room.ref)?.heatAreaRefs || []).includes(ref)))?.name || null;
  };
  const groups = new Map(), claimedPowers = new Set();
  const rows = modelRooms.slice().sort((a, b) => `${a.apartmentName || ''} ${a.name || ''}`.localeCompare(`${b.apartmentName || ''} ${b.name || ''}`, 'de', { numeric: true }));
  for (const geometry of rows) {
    const state = roomStates.get(geometry.ref) || {};
    const surfaces = objects.filter(object => object.kind === 'heatarea' && object.roomRef === geometry.ref);
    const areaRefs = [...new Set([geometry.heatAreaRef, ...(state.heatAreaRefs || []), ...surfaces.map(surface => surface.heatAreaRef),
      ...(snapshot?.heatAreas || []).filter(area => (area.roomRefs || []).includes(geometry.ref)).map(area => area.heatAreaRef)].filter(Boolean))];
    const heatAreas = areaRefs.map(heatAreaRef => {
      const area = areaStates.get(heatAreaRef) || { heatAreaRef };
      const heatingType = homeTwinHeatingType(objects, heatAreaRef, area.heating_type);
      const power = area.thermalPower, owner = power?.ownerHeatAreaRef;
      const powerKey = power?.groupId ? `group:${power.groupId}` : `area:${heatAreaRef}`;
      const ownPower = !owner || owner === heatAreaRef;
      const showPower = ownPower && !claimedPowers.has(powerKey);
      if (showPower && power?.valueW != null) claimedPowers.add(powerKey);
      return { ...area, heatAreaRef, heating_type: heatingType, name: area.name || heatingNames[heatingType] || 'Heizfläche', showPower,
        ownerName: ownerLabel(owner), floorOwnerName: ownerLabel(area.floorTemperatureEstimate?.ownerHeatAreaRef) };
    });
    const apartmentRef = geometry.apartmentRef || `unassigned:${floorRef}`;
    if (!groups.has(apartmentRef)) groups.set(apartmentRef, { apartmentRef, apartmentName: geometry.apartmentName || 'Räume ohne Appartementzuordnung', rooms: [] });
    groups.get(apartmentRef).rooms.push({ ...state, ref: geometry.ref, name: geometry.name || 'Raum', apartmentRef, floorRef, heatAreas });
  }
  return [...groups.values()];
}
